import React from "react";
import MaterialIcon from "../../../components/MaterialIcon";
import { PrimaryButton, SecondaryButton } from "./CockpitPrimitives";
import { fill, formatFromNow } from "../cockpitFormat";

/**
 * Kjuup-Email status banner (Kjuup.io).
 *   - no mailbox        -> "E-Mail verbinden"
 *   - client mode       -> accounts, last sync, briefing hour
 *   - reports mode      -> rooms with daily report, automatic todos
 * `children` renders full-width under the status row (reports mode: the
 * incoming-email assignment list).
 */
const isErrorStatus = (status) => String(status || "").toLowerCase().includes("error");

export default function CockpitEmailBanner({ strings, language, email, onConnect, onOpenInbox, onOpenSettings, onOpenRooms, children }) {
  const accounts = email?.accounts || [];
  const hasMailbox = accounts.length > 0;
  const hour = String(email?.dailyBriefingHour != null ? email.dailyBriefingHour : 18).padStart(2, "0");
  const reportsMode = email?.emailMode === "reports";
  const lastSync = accounts
    .map((a) => (a.lastSyncAt ? new Date(a.lastSyncAt).getTime() : 0))
    .reduce((m, t) => Math.max(m, t), 0);
  const hasError = accounts.some((a) => isErrorStatus(a.status));

  let title;
  let text;
  let cta;
  let secondary;

  if (!hasMailbox) {
    title = strings.cockpit_email_none_title || "Noch kein E-Mail-Konto verbunden";
    text = reportsMode
      ? strings.cockpit_email_none_reports || "Verbinde dein Postfach, damit Kjuup eingehende E-Mails deinen Projekten zuordnet und daraus Tagesberichte mit Aufgaben erstellt."
      : strings.cockpit_email_none_client || "Verbinde Gmail, Outlook oder IMAP, um E-Mails direkt in Kjuup.io zu lesen, zuzuordnen und zu beantworten.";
    cta = <PrimaryButton onClick={onConnect}>{strings.cockpit_email_connect || "E-Mail verbinden"}</PrimaryButton>;
  } else if (reportsMode) {
    title = strings.cockpit_email_reports_title || "Bisheriger E-Mail-Client";
    text = fill(
      strings.cockpit_email_reports_text ||
        "Kein Postfach in Kjuup.io · {{rooms}} Projekte mit Tagesbericht um {{hour}}:00 Uhr · Eingehende E-Mails werden deinen Projekten zugeordnet.",
      { rooms: email?.reportRoomsCount || 0, hour }
    );
    cta = <SecondaryButton icon="folder_open" onClick={onOpenRooms}>{strings.cockpit_email_reports_rooms || "Projekte verwalten"}</SecondaryButton>;
    secondary = <SecondaryButton icon="settings" onClick={onOpenSettings}>{strings.cockpit_email_settings || "E-Mail-Einstellungen"}</SecondaryButton>;
  } else {
    title = hasError
      ? strings.cockpit_email_error_title || "Verbindung prüfen"
      : strings.cockpit_email_client_title || "Empfang eingerichtet";
    const parts = [];
    parts.push(
      fill(
        accounts.length === 1
          ? strings.cockpit_email_accounts_n || "{{n}} Konto verbunden"
          : strings.cockpit_email_accounts_n_many || strings.cockpit_email_accounts_n || "{{n}} Konten verbunden",
        { n: accounts.length }
      )
    );
    if (lastSync) parts.push(fill(strings.cockpit_email_last_sync || "letzter Abruf {{when}}", { when: formatFromNow(lastSync, language) }));
    parts.push(fill(strings.cockpit_email_report_at || "Tagesbericht um {{hour}}:00 Uhr", { hour }));
    text = parts.join(" · ");
    cta = <PrimaryButton onClick={onOpenInbox}>{strings.cockpit_email_open_inbox || "Postfach öffnen"}</PrimaryButton>;
    secondary = <SecondaryButton icon="settings" onClick={onOpenSettings}>{strings.cockpit_email_settings || "E-Mail-Einstellungen"}</SecondaryButton>;
  }

  return (
    <section className={`cockpit-card cockpit-banner${hasError ? " cockpit-banner--warn" : ""}`}>
      <div className="cockpit-banner-row">
        <div className="cockpit-banner-icon">
          <MaterialIcon icon={hasMailbox ? "mark_email_read" : "mail"} size={22} color="currentColor" weight={400} />
        </div>
        <div className="cockpit-banner-text">
          <div className="cockpit-eyebrow cockpit-eyebrow--accent">{strings.cockpit_email_eyebrow || "Kjuup-Email"}</div>
          <div className="cockpit-banner-title">{title}</div>
          <div className="cockpit-banner-sub">{text}</div>
          {hasMailbox ? (
            <ul className="cockpit-banner-accounts" aria-label={strings.cockpit_email_accounts_label || "Verbundene E-Mail-Adressen"}>
              {accounts.map((a) => {
                const err = isErrorStatus(a.status);
                const hint = [
                  a.displayName,
                  a.provider ? String(a.provider).toUpperCase() : null,
                  err ? strings.cockpit_email_error_title || "Verbindung prüfen" : null,
                  a.lastSyncAt
                    ? fill(strings.cockpit_email_last_sync || "letzter Abruf {{when}}", { when: formatFromNow(new Date(a.lastSyncAt).getTime(), language) })
                    : null,
                ].filter(Boolean).join(" · ");
                return (
                  <li
                    key={a.id || a.email}
                    className={`cockpit-banner-account${err ? " cockpit-banner-account--error" : ""}`}
                    title={hint || undefined}
                  >
                    <MaterialIcon icon={err ? "error" : "alternate_email"} size={14} color="currentColor" weight={400} />
                    <span className="cockpit-banner-account-mail">{a.email}</span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        <div className="cockpit-banner-aside">
          <div className="cockpit-banner-actions">
            {secondary}
            {cta}
          </div>
        </div>
      </div>
      {children ? <div className="cockpit-banner-body">{children}</div> : null}
    </section>
  );
}
